import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { setAuth } from '../../lib/auth';
import { executeRecaptcha } from '../../lib/recaptcha';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowRight, ShieldCheck, Check, X } from 'lucide-react';
import { passwordError, PASSWORD_RULES, MAX_FIELD_LEN } from '../../lib/passwordPolicy';
import { isGenericEmail, BUSINESS_EMAIL_MESSAGE } from '../../lib/businessEmail';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#F25022" d="M1 1h10.5v10.5H1z"/>
    <path fill="#00A4EF" d="M1 12.5h10.5V23H1z"/>
    <path fill="#7FBA00" d="M12.5 1H23v10.5H12.5z"/>
    <path fill="#FFB900" d="M12.5 12.5H23V23H12.5z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROFESSIONS = [
  'Procurement', 'UBM Lead', 'Project Manager', 'Account Manager',
  'Finance', 'IT', 'Accounts Payable', 'Business Owner', 'Consultant',
];

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [oauthStatus, setOauthStatus] = useState({ google: { enabled: false }, microsoft: { enabled: false } });
  const [registration, setRegistration] = useState({ enabled: true, closed_message: '', checked: false });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    profession: '',
  });

  // Show OAuth error from URL if redirected back
  useEffect(() => {
    const err = searchParams.get('error');
    const provider = searchParams.get('provider');
    if (err) {
      const messages = {
        oauth_disabled: `${provider || 'Provider'} sign-in is not configured yet. Ask your admin to enable it or use email below.`,
        oauth_no_email: 'Your account did not share an email address. Please use email + password.',
        oauth_error: 'Sign-in was cancelled or blocked.',
        oauth_token_exchange: 'Could not complete sign-in with the provider.',
        oauth_state: 'Session expired — please try again.',
      };
      toast.error(messages[err] || `Sign-in failed (${err})`);
    }
  }, [searchParams]);

  // Load OAuth provider status
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/oauth/status');
        setOauthStatus(data?.providers || {});
      } catch (e) {}
    })();
  }, []);

  // Load registration status (may be disabled globally by Super Admin)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auth/registration-status');
        setRegistration({
          enabled: !!data?.enabled,
          closed_message: data?.closed_message || 'New sign-ups are currently invite-only.',
          checked: true,
          blocked_domains: data?.blocked_domains || [],
          allowed_domains: data?.allowed_domains || [],
        });
      } catch (e) {
        setRegistration({ enabled: true, closed_message: '', checked: true });
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailVal = formData.email.trim();
    if (isGenericEmail(emailVal, registration)) {
      setEmailError(BUSINESS_EMAIL_MESSAGE);
      toast.error(BUSINESS_EMAIL_MESSAGE);
      return;
    }
    setEmailError('');
    const pwErr = passwordError(formData.password);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }
    if (!formData.company.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    if (!formData.profession) {
      toast.error('Please select your role');
      return;
    }
    setLoading(true);
    try {
      const recaptcha_token = await executeRecaptcha('signup');
      const plan = (searchParams.get('plan') || '').toLowerCase();
      const src = searchParams.get('src') || '';
      const { data } = await api.post('/auth/signup', {
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        company: formData.company.trim(),
        profession: formData.profession,
        recaptcha_token,
      });
      // Remember paid-plan intent so we can nudge them to upgrade once inside
      // (registration itself is always free — no card wall).
      if (plan && plan !== 'free') {
        localStorage.setItem('bp_upgrade_intent', plan);
      }
      // Preview: email verification bypassed → auto-login straight to dashboard.
      if (data?.verification_bypassed && data?.login) {
        setAuth(data.login.access_token, data.login.user, data.login.accounts);
        toast.success('Welcome to BillPortal!');
        navigate('/dashboard');
        return;
      }
      toast.success('Check your email for the code.');
      // Carry plan intent + test_otp through the email-verification step.
      const otpParam = data?.test_otp ? `&test_otp=${data.test_otp}` : '';
      const planParam = plan ? `&plan=${plan}` : '';
      const srcParam = src ? `&src=${encodeURIComponent(src)}` : '';
      navigate(`/verify-email?user_id=${data.user_id}&email=${encodeURIComponent(data.email)}${otpParam}${planParam}${srcParam}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const oauthUrl = (provider) =>
    `${BACKEND_URL}/api/auth/oauth/${provider}/login`;

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-5">
      {/* Left promo — hidden on small screens */}
      <div className="hidden lg:flex lg:col-span-2 bg-[hsl(221,100%,12%)] text-white p-12 flex-col justify-between">
        <div>
          <div className="inline-flex items-center gap-3" data-testid="signup-brand">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-10 w-10" />
            <span className="text-2xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>BillPortal</span>
          </div>
          <div className="mt-2 text-slate-400 text-sm">Utility Bill & Expense Management</div>
        </div>
        <div className="space-y-8">
          <h1 className="text-4xl lg:text-5xl font-black leading-[1.05] tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Start free.<br/>
            <span className="text-blue-200">Parse your first bill</span><br/>
            in 5 minutes.
          </h1>
          <ul className="space-y-3 text-slate-300 text-sm">
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-blue-300"/> No credit card required</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-blue-300"/> Set up unlimited properties, units & meters</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-blue-300"/> 5 bills parsed / month · $5 in AI credits</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-blue-300"/> Your AI teammate guides your onboarding</li>
          </ul>
        </div>
        <div className="text-xs text-slate-500">
          © BillPortal. Enterprise-grade utility ops on prepaid credits.
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-3 flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-md">
      <div className="mb-8">
        <h2 className="text-3xl font-black tracking-tighter" data-testid="signup-title" style={{ fontFamily: 'Chivo, sans-serif' }}>Create your account</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Free forever for up to 5 accounts/month · no card needed
        </p>
      </div>

          {(oauthStatus.google?.enabled || oauthStatus.microsoft?.enabled) && (
            <div className="space-y-2 mb-6">
              {oauthStatus.google?.enabled && (
                <a
                  href={oauthUrl('google')}
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-none border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium transition-colors"
                  data-testid="signup-google-btn"
                >
                  <GoogleIcon />
                  <span>Continue with Google</span>
                </a>
              )}
              {oauthStatus.microsoft?.enabled && (
                <a
                  href={oauthUrl('microsoft')}
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-none border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium transition-colors"
                  data-testid="signup-microsoft-btn"
                >
                  <MicrosoftIcon />
                  <span>Continue with Microsoft</span>
                </a>
              )}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"/>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs uppercase text-slate-500 tracking-wide">
                    or sign up with email
                  </span>
                </div>
              </div>
            </div>
          )}

          {registration.checked && !registration.enabled ? (
            <div className="bg-slate-50 border border-slate-200 p-8 text-center" data-testid="signup-closed">
              <div className="h-12 w-12 mx-auto bg-slate-200 flex items-center justify-center mb-4">
                <ShieldCheck className="h-6 w-6 text-slate-600"/>
              </div>
              <div className="text-lg font-black tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Registration is invite-only
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                {registration.closed_message}
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                >
                  Already have an account? Sign in →
                </Link>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-wide font-medium">Full name</Label>
              <Input
                id="name"
                data-testid="signup-name-input"
                type="text"
                required
                maxLength={MAX_FIELD_LEN}
                autoComplete="name"
                className="rounded-none mt-1"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wide font-medium">Work email</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                required
                maxLength={MAX_FIELD_LEN}
                autoComplete="email"
                className="rounded-none mt-1"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (emailError) setEmailError(''); }}
                onBlur={(e) => setEmailError(isGenericEmail(e.target.value.trim(), registration) ? BUSINESS_EMAIL_MESSAGE : '')}
              />
              {emailError && (
                <p className="text-xs text-red-600 mt-1.5" data-testid="signup-email-error">{emailError}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wide font-medium">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                required
                minLength={8}
                maxLength={MAX_FIELD_LEN}
                autoComplete="new-password"
                className="rounded-none mt-1"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <ul className="mt-2 grid grid-cols-1 gap-1" data-testid="signup-password-rules">
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(formData.password || '');
                  return (
                    <li
                      key={rule.key}
                      className={`flex items-center gap-1.5 text-[11px] ${ok ? 'text-green-600' : 'text-slate-500'}`}
                      data-testid={`signup-pw-rule-${rule.key}`}
                    >
                      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <Label htmlFor="company" className="text-xs uppercase tracking-wide font-medium">
                Company <span className="text-red-500">*</span>
              </Label>
              <Input
                id="company"
                data-testid="signup-company-input"
                type="text"
                required
                maxLength={MAX_FIELD_LEN}
                autoComplete="organization"
                className="rounded-none mt-1"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Your company / organization name"
              />
              <p className="mt-1 text-[11px] text-slate-500">This becomes your workspace (organization) name.</p>
            </div>

            <div>
              <Label htmlFor="profession" className="text-xs uppercase tracking-wide font-medium">
                Your role <span className="text-red-500">*</span>
              </Label>
              <select
                id="profession"
                data-testid="signup-profession-select"
                required
                className="w-full rounded-none mt-1 border border-slate-200 h-10 px-3 text-sm bg-white focus:border-primary focus:outline-none"
                value={formData.profession}
                onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
              >
                <option value="" disabled>Select your role…</option>
                {PROFESSIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <Button
              type="submit"
              data-testid="signup-submit-btn"
              className="w-full rounded-none h-11 font-bold uppercase tracking-wide"
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Creating your account…</>
              ) : (
                <>Start free <ArrowRight className="h-4 w-4 ml-2"/></>
              )}
            </Button>
          </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-600" data-testid="signup-login-link">
            Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
          </p>

          <p className="mt-8 text-center text-[11px] text-slate-500 leading-relaxed">
            By continuing you agree to our Terms and Privacy Policy.
            <br/>Free plan: 5 bills parsed / month · $5 in AI credits (one-time).
          </p>
        </div>
      </div>
    </div>
  );
}
